/**
 * Price calibration screen (COCKPIT-VISION.md build-order item 7,
 * 2026-08-25 build; prices re-sourced from the tenant's own SP-API
 * connection off Keepa the same day — Faraz's decision) —
 * /app/price-calibration, visible only when the 'amazon' module is
 * enabled. For every product: Your price (our own SP-API offer — the
 * verdict/margin anchor), Buy box (Amazon's featured offer, context only,
 * with a you-hold-it marker + delta vs Your price), and suggested minimum
 * and maximum (target) prices from cost of goods + the tenant's margin
 * settings. Prices update automatically on the hourly Amazon sync — no
 * manual refresh button; ALL row logic (filter/sort/format/scenario math)
 * is pure helpers in lib/price-calibration.ts and lib/profit-scenario.ts,
 * unit-tested there.
 *
 * Responsive decision surface (existing-inventory velocity + scenario plan,
 * Task 4, 2026-08-26/31 — docs/plans/2026-08-26-price-calibration-existing-
 * inventory-design.md): the old dense density table (Template: AmazonPage)
 * is replaced by a card-list — each row is PriceCalibrationRow.tsx, which
 * renders a reduced desktop decision grid AND a stacked tablet/mobile card
 * from the same data (see that file's module doc). Detailed math/
 * provenance stays inside the row's per-SKU expansion.
 *
 * Streamlined toolbar (Task B, 2026-09-03 —
 * docs/plans/2026-09-03-pricing-strategies-design.md §2): the control row
 * is now Search + a Filter menu (inventory scope + verdict, radio groups
 * in one Radix dropdown) + a Sort menu (direction toggle + sort key, also
 * radio groups) + a right-aligned Strategy menu slot (Task C wires the
 * menu itself in). A state line of small removable chips below the control
 * row keeps the active scope/filter/sort/search legible once their menus
 * close — see CalibrationList's module doc for the chip/menu details.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import {
  Button,
  Card,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
} from '../../design';
import {
  agoLabel,
  buildScenarioSummary,
  canRefreshCompetitorStock,
  DEFAULT_INVENTORY_SCOPE,
  DEFAULT_PRICE_CALIBRATION_FILTER,
  filterTriggerLabel,
  INVENTORY_SCOPE_OPTIONS,
  inventoryContextUnavailableNote,
  latestCompetitionCapturedAt,
  money,
  noPricesFetched,
  priceCalibrationFilterCounts,
  PRICE_CALIBRATION_FILTER_PILLS,
  searchPriceCalibrationRows,
  signedMoney,
  sortPriceCalibrationRows,
  SORT_KEY_LABELS,
  sortTriggerLabel,
  usePriceCalibration,
  useRefreshCompetitorStock,
  visiblePriceCalibrationRows,
  type CompetitorRefreshState,
  type PriceCalibrationFilterKey,
  type PriceCalibrationScenarioSummary,
  type PriceCalibrationSortDirection,
  type PriceCalibrationSortKey,
} from '../../lib/price-calibration';
import type { InventoryScope } from '../../lib/profit-scenario';
import {
  applyStrategy,
  ASK_AI_MENU_ITEM,
  SKIP_LABELS,
  STRATEGY_OPTIONS,
  strategyChipLabel,
  strategyResultSummary,
  type ReasonTag,
  type StrategyKey,
  type StrategyResult,
} from '../../lib/price-strategies';
import { useStoreSettings } from '../../lib/storeSettings';
import { useMe, useModuleEnabled } from '../../lib/auth';
import { AppShell } from '../../components/AppShell';
import { Skeleton, SkeletonLine } from '../../components/Skeleton';
import { PriceCalibrationRow as PriceCalibrationRowView } from './PriceCalibrationRow';
import { ScenarioSummaryStrip } from './ScenarioSummaryStrip';
import type { PriceCalibrationRow } from '@sourcito/schemas';
import type { ReactNode } from 'react';

/** Tenant margin thresholds (fractions 0-1) — null until store settings
 *  load. Shared shape for every row's math card + verdict recompute. */
type MarginThresholds = { minFrac: number; targetFrac: number } | null;

/**
 * Live seller-SKU scenario map (velocity + scenario plan Task 3,
 * 2026-08-26/31). Several SKUs can each carry an independent "try a price"
 * simultaneously; the map only holds DEVIATIONS from the live price
 * (lib/price-calibration.ts's proposedPriceForRow falls back to
 * `row.yourPriceCents` for any SKU with no entry). Lives in CalibrationList
 * state, NEVER persisted, NEVER fed into a mutation or the query cache —
 * reset on unmount/nav.
 */
type ScenarioMap = Map<string, number>;

export function PriceCalibrationPage() {
  const enabled = useModuleEnabled('amazon');
  const { data, isLoading, isError } = usePriceCalibration(enabled);
  // Competitor stock refresh button (2026-10-05) — owner/operator only, the
  // same pair the API route allows. Other roles never get the state, so the
  // toolbar never renders the button for them.
  const { data: me } = useMe();
  const competitorRefresh = useRefreshCompetitorStock();
  // Tenant's configured margins (fractions 0-1) — feeds every row's math
  // card and verdict recompute. null until settings load (never a stale/
  // hardcoded pair).
  const { data: storeSettings } = useStoreSettings();
  const thresholds: MarginThresholds = storeSettings
    ? { minFrac: storeSettings.minMarginPct, targetFrac: storeSettings.targetMarginPct }
    : null;

  if (!enabled) {
    return (
      <AppShell>
        <main className="max-w-3xl mx-auto px-8 py-16">
          <Card>
            <p className="text-sm text-fg-secondary">
              The Amazon module isn&rsquo;t enabled for this workspace.{' '}
              <Link to="/app/brands" className="text-accent-fg hover:underline">
                Back to Brands
              </Link>
            </p>
          </Card>
        </main>
      </AppShell>
    );
  }

  const rows = data?.rows ?? [];

  return (
    <AppShell>
      <header className="border-b border-border-subtle">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 py-4">
          <h1 className="text-xl font-medium leading-tight">Price Calibration</h1>
          <p className="text-xs text-fg-tertiary">
            {rows.length > 0 ? `${rows.length} product${rows.length === 1 ? '' : 's'} · ` : ''}
            Prices update automatically with the hourly Amazon sync.
          </p>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 sm:px-8 py-8">
        {isLoading && (
          <>
            <p className="sr-only" role="status" aria-live="polite">
              Loading price calibration…
            </p>
            <PriceCalibrationSkeleton />
          </>
        )}
        {isError && <p className="text-sm text-negative">Could not load price calibration.</p>}
        {!isLoading && !isError && rows.length === 0 && (
          <Card>
            <p className="text-sm text-fg-secondary">
              No Amazon inventory synced yet — sync from the Amazon page first.
            </p>
          </Card>
        )}
        {!isLoading && !isError && rows.length > 0 && (
          <CalibrationList
            rows={rows}
            thresholds={thresholds}
            inventoryContextAvailable={data?.inventoryContextAvailable ?? false}
            competitorRefresh={
              canRefreshCompetitorStock(me?.user.role) ? competitorRefresh : undefined
            }
          />
        )}
      </main>
    </AppShell>
  );
}

// Coarse shimmer for the CalibrationList's card-list (PriceCalibrationRow
// renders a reduced desktop decision grid + a stacked mobile card from the
// same data — this mirrors both layouts' outer shape without reusing that
// component, since it's a live SKU row, not a placeholder). Toolbar/
// summary cards above this stay hidden entirely while loading; there's no
// row data yet to search, filter, sort, or summarize.
function PriceCalibrationSkeleton() {
  return (
    <div className="space-y-3" aria-hidden="true">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="border border-border-subtle rounded-xl bg-surface px-4 py-3">
          {/* Desktop — mirrors PriceCalibrationRow's reduced decision grid. */}
          <div className="hidden md:grid md:grid-cols-[minmax(180px,1.6fr)_minmax(150px,1fr)_minmax(140px,1fr)_minmax(160px,1.1fr)_minmax(120px,0.9fr)_auto] md:gap-4 md:items-center">
            <div className="flex items-center gap-3 min-w-0">
              <Skeleton className="h-10 w-10 rounded-md shrink-0" />
              <div className="min-w-0 flex-1 space-y-1.5">
                <SkeletonLine className="h-3.5 w-40" />
                <SkeletonLine className="h-3 w-24" />
              </div>
            </div>
            <SkeletonLine className="h-3.5 w-20" />
            <SkeletonLine className="h-3.5 w-20" />
            <SkeletonLine className="h-3.5 w-24" />
            <SkeletonLine className="h-3.5 w-20" />
            <Skeleton className="h-5 w-16 rounded-full" />
          </div>
          {/* Tablet/mobile — mirrors the stacked card. */}
          <div className="md:hidden flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-md shrink-0" />
            <div className="min-w-0 flex-1 space-y-1.5">
              <SkeletonLine className="h-3.5 w-2/3" />
              <SkeletonLine className="h-3 w-1/3" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Calibration decision list (Task 4, streamlined toolbar Task B —
// docs/plans/2026-09-03-pricing-strategies-design.md §2) — a control row of
// Search + a Filter menu (inventory scope + verdict radios) + a Sort menu
// (direction + sort-key radios) + a right-aligned Strategy slot, a state
// line of removable chips (scope, filter, sort, search, reset-tried-prices)
// reflecting that menu state once the menus close, live summary cards, and
// a stacked list of PriceCalibrationRow cards. Replaces the old dense
// CalibrationTable and, before that, the three-row scope-tabs/verdict-pills/
// six-sort-buttons toolbar.
//
// Sort direction is now independent of sort key (deliberate change from the
// old click-to-toggle behaviour): picking a new sort key in the Sort menu
// keeps whatever direction was already selected, since direction and key
// are two separate radio groups in the same menu rather than one button
// per key.
//
// Test-coverage assumption: the static-render suite
// (PriceCalibrationPage.toolbar.test.tsx) exercises only the closed-menu
// UI — Radix portals render null under `renderToStaticMarkup`, and the
// suite runs in plain node (no jsdom), so menu contents and the chips'
// click-to-change paths are not exercised there. They're covered instead by
// the pure label/state helpers in lib/price-calibration.ts (filterTriggerLabel,
// sortTriggerLabel, etc.), which are unit-tested directly.
// ─────────────────────────────────────────────────────────

export function CalibrationList({
  rows,
  thresholds,
  inventoryContextAvailable,
  initialStrategy,
  initialTriedPrices,
  competitorRefresh,
}: {
  rows: PriceCalibrationRow[];
  thresholds: MarginThresholds;
  inventoryContextAvailable: boolean;
  /** Competitor stock refresh state (see useRefreshCompetitorStock). The
   *  page passes it only to owner and operator; when it is absent the
   *  toolbar shows no refresh button. */
  competitorRefresh?: CompetitorRefreshState;
  /** Test-only: pre-applies a strategy on mount so a static render can
   *  assert the resulting chip/scenario state without simulating a menu
   *  click (Radix's dropdown content lives behind a Portal that never
   *  mounts under this suite's renderToStaticMarkup). Never passed by
   *  PriceCalibrationPage itself. */
  initialStrategy?: StrategyKey;
  /** Test-only: pre-seeds the scenario map on mount with no strategy/reason
   *  tags attached, so a static render can assert the chip state for a
   *  hand-edit-only tried price (no `activeStrategy`). Ignored when
   *  `initialStrategy` is also passed. Never passed by PriceCalibrationPage
   *  itself. */
  initialTriedPrices?: Map<string, number>;
}) {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<PriceCalibrationFilterKey>(DEFAULT_PRICE_CALIBRATION_FILTER);
  // Default sort: margin ascending, nulls last — bleeders on top.
  const [sortKey, setSortKey] = useState<PriceCalibrationSortKey>('margin');
  const [sortDirection, setSortDirection] = useState<PriceCalibrationSortDirection>('asc');
  // Math-card expand affordance, keyed on sellerSku.
  const [expandedSkus, setExpandedSkus] = useState<Set<string>>(new Set());
  // Existing-inventory scope (velocity + scenario plan Task 3) — which
  // physical units every total/row counts. Default matches the design
  // doc's default ("sellable now" = FBA fulfillable only).
  const [scope, setScope] = useState<InventoryScope>(DEFAULT_INVENTORY_SCOPE);
  // Pinned strip (founder UAT 2026-09-03): the summary cards scroll away
  // while editing rows far down the list. Observe the cards; once they are
  // above the viewport, ScenarioSummaryStrip pins the same numbers under
  // the app bar. No IntersectionObserver (old browser, jsdom) -> strip
  // stays hidden, page behaves exactly as before.
  const summaryCardsRef = useRef<HTMLDivElement>(null);
  const [summaryCardsOffscreen, setSummaryCardsOffscreen] = useState(false);
  useEffect(() => {
    const el = summaryCardsRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        setSummaryCardsOffscreen(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      },
      // Treat the sticky app bar (h-14 on md+) as the top edge.
      { rootMargin: '-56px 0px 0px 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Founder UAT finding 4 (2026-08-30): counts are scope-aware — a
  // zero-stock-in-scope row counts toward the "Out of stock" pill only,
  // matching exactly what clicking each pill reveals below.
  const counts = useMemo(() => priceCalibrationFilterCounts(rows, scope), [rows, scope]);

  const visibleRows = useMemo(() => {
    const searched = searchPriceCalibrationRows(rows, query);
    // Zero-stock-in-scope rows are hidden by default and shown only under
    // the explicit "Out of stock" pill (design doc: "if they are not in
    // stock what's the point of price calibration").
    const visible = visiblePriceCalibrationRows(searched, scope, filter);
    return sortPriceCalibrationRows(visible, sortKey, sortDirection);
  }, [rows, query, filter, sortKey, sortDirection, scope]);

  // Strategy menu state (pricing-strategies design, Task C —
  // docs/plans/2026-09-03-pricing-strategies-design.md §2). Applying a
  // strategy REPLACES any previous tried prices (it "fills the map"):
  // scenarios/reasonTags are always overwritten together with
  // activeStrategy, never merged with what was there before. All three are
  // seeded from ONE lazy useState below (`seed`, test-only) computed exactly
  // once at mount over the CURRENT visibleRows/scope — React only invokes a
  // useState initializer function on the first render, so this never
  // re-applies on a later re-render no matter how many times the component
  // body re-evaluates. `initialTriedPrices` is a second test-only hook, for
  // asserting the chip state for a hand-edit-only tried price with no
  // strategy active.
  const [seed] = useState<{
    activeStrategy: { key: StrategyKey; result: StrategyResult } | null;
    scenarios?: Map<string, number>;
  }>(() =>
    initialStrategy
      ? {
          activeStrategy: {
            key: initialStrategy,
            result: applyStrategy(initialStrategy, visibleRows, scope),
          },
        }
      : { activeStrategy: null, scenarios: initialTriedPrices }
  );
  const [activeStrategy, setActiveStrategy] = useState<{
    key: StrategyKey;
    result: StrategyResult;
  } | null>(() => seed.activeStrategy ?? null);
  // Reason tag per sellerSku (Strategy menu) — cleared for a SKU the moment
  // a hand edit (onSimulate) touches it, per the design's "hand edits keep
  // the price and clear the tag."
  const [reasonTags, setReasonTags] = useState<Map<string, ReasonTag>>(
    () => new Map(seed.activeStrategy?.result.reasons ?? [])
  );
  // Live multi-SKU scenario map — see the ScenarioMap type doc above:
  // NEVER persisted, NEVER written to a mutation or the query cache.
  const [scenarios, setScenarios] = useState<ScenarioMap>(
    () => new Map(seed.activeStrategy?.result.scenarios ?? seed.scenarios ?? [])
  );

  // Live top totals — recomputed synchronously from the current scope and
  // every entry in the scenario map on every render, over the rows
  // currently on screen (filtered/searched), same "what you're looking at"
  // convention the old projection strip used.
  const scenarioSummary: PriceCalibrationScenarioSummary = useMemo(
    () => buildScenarioSummary(visibleRows, scope, scenarios),
    [visibleRows, scope, scenarios]
  );

  // Strategy chip summary (Task C): only the MONEY fields (profit/sell-
  // through delta, loss taken) are recomputed against the LIVE scenario map
  // (not the strategy's own result.scenarios) — a hand edit after applying
  // a strategy is reflected in those dollar figures immediately. `changed`
  // is NOT live: it's `result.scenarios.size`, frozen at the moment the
  // strategy was applied — see strategyResultSummary's `scenarios` param doc
  // in price-strategies.ts.
  const strategySummary = useMemo(
    () =>
      activeStrategy
        ? strategyResultSummary(activeStrategy.result, visibleRows, scope, scenarios)
        : null,
    [activeStrategy, visibleRows, scope, scenarios]
  );
  // The active strategy stays applied (and its chip stays shown) across
  // scope/filter/search changes — it was computed once over the rows/scope
  // at click time, same as the tried-price map it wrote into.
  const activeStrategyOption = activeStrategy
    ? STRATEGY_OPTIONS.find((o) => o.key === activeStrategy.key)
    : undefined;
  const activeStrategyChipLabel =
    activeStrategyOption && strategySummary
      ? strategyChipLabel(activeStrategyOption, strategySummary)
      : null;

  function toggleExpanded(sellerSku: string) {
    setExpandedSkus((prev) => {
      const next = new Set(prev);
      if (next.has(sellerSku)) next.delete(sellerSku);
      else next.add(sellerSku);
      return next;
    });
  }

  // A scenario entry only ever holds a DEVIATION from the live price —
  // PriceCalibrationRow's stepper already converts "typed price equals the
  // live price" to `null` before calling this, so `null` here always means
  // "reset." Either way — a real hand-typed price, or a reset — this is a
  // HAND EDIT: it keeps (or clears) the price but always clears that SKU's
  // Strategy reason tag (design: "hand edits keep the price and clear the
  // tag"), since the tag no longer describes why the price is what it is.
  function onSimulate(sellerSku: string, priceCents: number | null) {
    setScenarios((prev) => {
      const next = new Map(prev);
      if (priceCents === null) {
        next.delete(sellerSku);
      } else {
        next.set(sellerSku, priceCents);
      }
      return next;
    });
    setReasonTags((prev) => {
      if (!prev.has(sellerSku)) return prev;
      const next = new Map(prev);
      next.delete(sellerSku);
      return next;
    });
  }

  function resetAllScenarios() {
    setScenarios(new Map());
    setActiveStrategy(null);
    setReasonTags(new Map());
  }

  // Strategy menu selection (Task C): applyStrategy REPLACES any previous
  // tried prices/reasons wholesale — it "fills the map," it doesn't merge
  // into whatever was there (a prior strategy's picks, or hand edits).
  function applyStrategyChoice(key: StrategyKey) {
    const result = applyStrategy(key, visibleRows, scope);
    setScenarios(new Map(result.scenarios));
    setActiveStrategy({ key, result });
    setReasonTags(new Map(result.reasons));
  }

  function toggleSortDirection() {
    setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
  }

  const filterLabel = filterTriggerLabel(scope, filter, counts);
  const sortLabel = sortTriggerLabel(sortKey, sortDirection);
  const scopeChipLabel = INVENTORY_SCOPE_OPTIONS.find((o) => o.key === scope)?.label ?? scope;
  const otherScope: InventoryScope = scope === 'sellable-now' ? 'all-owned' : 'sellable-now';
  const otherScopeLabel =
    INVENTORY_SCOPE_OPTIONS.find((o) => o.key === otherScope)?.label ?? otherScope;
  const activeFilterPill = PRICE_CALIBRATION_FILTER_PILLS.find((p) => p.key === filter);
  const trimmedQuery = query.trim();
  const competitorCheckedAt = latestCompetitionCapturedAt(rows);
  const refreshLabel = !competitorRefresh?.isRunning
    ? 'Refresh competitor stock'
    : competitorRefresh.remaining === null
      ? 'Refreshing'
      : `Refreshing, ${competitorRefresh.remaining.toLocaleString('en-US')} left`;

  return (
    <div>
      {/* Control row (toolbar streamlining, Task B — docs/plans/2026-09-03-
          pricing-strategies-design.md §2): search, a Filter menu merging
          the old scope tabs + verdict pills, a Sort menu replacing the old
          six sort buttons, and a right-aligned slot Task C wires the
          Strategy menu into. */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search
            className="h-4 w-4 text-fg-tertiary absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
            aria-hidden="true"
          />
          <Input
            inputSize="sm"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search SKU or product name..."
            className="pl-9"
          />
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger>{filterLabel}</DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuLabel>Inventory scope</DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={scope}
              onValueChange={(value) => setScope(value as InventoryScope)}
            >
              {INVENTORY_SCOPE_OPTIONS.map((opt) => (
                <DropdownMenuRadioItem
                  key={opt.key}
                  value={opt.key}
                  onSelect={(e) => e.preventDefault()}
                >
                  {opt.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Show</DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={filter}
              onValueChange={(value) => setFilter(value as PriceCalibrationFilterKey)}
            >
              {PRICE_CALIBRATION_FILTER_PILLS.map((p) => (
                <DropdownMenuRadioItem key={p.key} value={p.key}>
                  {p.label} · {counts[p.key]}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger>{sortLabel}</DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuRadioGroup
              value={sortDirection}
              onValueChange={(value) => setSortDirection(value as PriceCalibrationSortDirection)}
            >
              <DropdownMenuRadioItem value="asc">Ascending</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="desc">Descending</DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator />
            <DropdownMenuRadioGroup
              value={sortKey}
              onValueChange={(value) => setSortKey(value as PriceCalibrationSortKey)}
            >
              {(Object.keys(SORT_KEY_LABELS) as PriceCalibrationSortKey[]).map((key) => (
                <DropdownMenuRadioItem key={key} value={key}>
                  {SORT_KEY_LABELS[key]}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Competitor stock refresh (2026-10-05) — owner/operator only. The
            button runs the refresh to the end (see useRefreshCompetitorStock);
            the muted text says how old the newest snapshot is. */}
        {competitorRefresh && (
          <div className="flex items-center gap-2 flex-wrap" data-slot="competitor-refresh">
            <Button
              variant="secondary"
              size="sm"
              disabled={competitorRefresh.isRunning}
              onClick={() => void competitorRefresh.start()}
              className="text-xs bg-surface text-fg-secondary border-border-subtle hover:bg-surface hover:text-fg-primary hover:border-border-default"
            >
              {refreshLabel}
            </Button>
            {competitorCheckedAt && (
              <span className="text-xs text-fg-tertiary">
                {`Competitor stock checked ${agoLabel(competitorCheckedAt, new Date())}`}
              </span>
            )}
            {competitorRefresh.error && (
              <span className="text-xs text-negative" role="alert">
                {competitorRefresh.error}
              </span>
            )}
          </div>
        )}

        {/* Strategy menu (Task C, §2) — right-aligned, primary-styled per
            the design doc ("Strategy ▾ ... right-aligned, primary-styled").
            Disabled with an explanatory title when inventoryContextAvailable
            is false, same as the summary cards below going dark: a strategy
            can't size its own profit/loss numbers without the same
            inventory context those cards need. */}
        <div className="ml-auto" data-slot="strategy-menu">
          <DropdownMenu>
            <DropdownMenuTrigger
              disabled={!inventoryContextAvailable}
              title={
                inventoryContextAvailable ? undefined : 'Scenario totals are not available yet'
              }
              className={cn(
                'bg-accent text-fg-on-accent border-accent',
                'hover:bg-accent-hover hover:text-fg-on-accent hover:border-accent',
                'data-[state=open]:bg-accent-hover data-[state=open]:text-fg-on-accent data-[state=open]:border-accent',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              Strategy
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[260px]">
              {STRATEGY_OPTIONS.map((option) => (
                <DropdownMenuItem key={option.key} onSelect={() => applyStrategyChoice(option.key)}>
                  <div>
                    <p className="text-fg-primary">{option.label}</p>
                    <p className="text-[11px] text-fg-tertiary mt-0.5">{option.description}</p>
                  </div>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                disabled={ASK_AI_MENU_ITEM.disabled}
                data-slot={ASK_AI_MENU_ITEM.dataSlot}
              >
                {ASK_AI_MENU_ITEM.label}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* State line — small removable chips reflecting the control row
          above, so the active scope/filter/sort/search stay legible once
          their menus close. */}
      <div className="flex items-center gap-1.5 flex-wrap mt-2">
        <Chip
          ariaLabel={`Inventory scope: ${scopeChipLabel}. Switch to ${otherScopeLabel}.`}
          onClick={() => setScope(otherScope)}
        >
          {scopeChipLabel}
        </Chip>

        {filter !== 'all' && activeFilterPill && (
          <Chip
            ariaLabel={`Clear filter: ${activeFilterPill.label}`}
            onClick={() => setFilter(DEFAULT_PRICE_CALIBRATION_FILTER)}
          >
            {activeFilterPill.label} · {counts[filter]}
            <span aria-hidden="true">×</span>
          </Chip>
        )}

        <Chip
          ariaLabel={`Sort: ${SORT_KEY_LABELS[sortKey]}, ${
            sortDirection === 'asc' ? 'ascending' : 'descending'
          }. Click to toggle direction.`}
          onClick={toggleSortDirection}
        >
          {SORT_KEY_LABELS[sortKey]} {sortDirection === 'asc' ? '↑' : '↓'}
        </Chip>

        {trimmedQuery && (
          <Chip ariaLabel={`Clear search "${trimmedQuery}"`} onClick={() => setQuery('')}>
            &ldquo;{trimmedQuery}&rdquo;
            <span aria-hidden="true">×</span>
          </Chip>
        )}

        {activeStrategyChipLabel && (
          <Chip
            ariaLabel={`Reset strategy: ${activeStrategyChipLabel}`}
            onClick={resetAllScenarios}
            accent
          >
            {activeStrategyChipLabel}
            <span aria-hidden="true">×</span>
          </Chip>
        )}

        {scenarios.size > 0 && !activeStrategy && (
          <Chip ariaLabel="Reset all tried prices" onClick={resetAllScenarios} accent>
            Reset all tried prices ({scenarios.size})
          </Chip>
        )}
      </div>

      {/* Strategy skipped-reason breakdown (Task C, §2) — one muted line
          under the chip state line, only when the active strategy actually
          left something alone. */}
      {activeStrategy && strategySummary && strategySummary.skipped.length > 0 && (
        <p className="text-xs text-fg-tertiary mt-1">
          left alone:{' '}
          {strategySummary.skipped.map((s) => `${s.count} ${SKIP_LABELS[s.why]}`).join(', ')}
        </p>
      )}

      {/* Deploy-skew guard (new web + old API): an API build that predates
          Task 2 sends no inventory/velocity fields at all, and the schema's
          `.default(0)` guards would otherwise make every row read as "0
          selected units" — the scenario cards would show a confidently
          wrong $0 for a real, stocked portfolio. Skip the cards and say so
          plainly instead of computing totals from data that was never
          fetched. */}
      {inventoryContextUnavailableNote({ inventoryContextAvailable }) ? (
        <p className="text-xs text-warning bg-warning/[0.08] border border-warning/30 rounded-lg px-3 py-2 mt-3">
          {inventoryContextUnavailableNote({ inventoryContextAvailable })}
        </p>
      ) : (
        <>
          <div ref={summaryCardsRef}>
            <ScenarioSummaryCards summary={scenarioSummary} includedRowCount={visibleRows.length} />
          </div>
          <ScenarioSummaryStrip
            summary={scenarioSummary}
            includedRowCount={visibleRows.length}
            visible={summaryCardsOffscreen}
          />
        </>
      )}

      {/* Pre-first-sync nudge — only when NO row has a your-price yet
          (checked against the full row set, not the filtered/searched view,
          so filtering to the "No price" pill doesn't spuriously trigger
          this). Once even one row has a price, the per-row freshness dates
          and 'No price' pills already communicate the rest. Disappears for
          good the moment this tenant's first post-deploy hourly sync lands
          a price — there is no manual refresh to fall back on. */}
      {noPricesFetched(rows) && (
        <p className="text-xs text-info bg-info/[0.08] border border-info/30 rounded-lg px-3 py-2 mt-3">
          Prices haven&rsquo;t synced yet — they&rsquo;ll appear automatically after the next hourly
          Amazon sync.
        </p>
      )}

      {/* Decision list — no fixed wide min-width; wide content (the
          expanded math card) scrolls in its own container instead of the
          page. */}
      <div className="mt-3 space-y-3">
        {visibleRows.length === 0 ? (
          <Card>
            <p className="text-sm text-fg-secondary">No matches.</p>
          </Card>
        ) : (
          visibleRows.map((row) => (
            <PriceCalibrationRowView
              key={row.sellerSku}
              row={row}
              scope={scope}
              thresholds={thresholds}
              expanded={expandedSkus.has(row.sellerSku)}
              onToggleExpanded={() => toggleExpanded(row.sellerSku)}
              simulatedCents={scenarios.get(row.sellerSku) ?? null}
              onSimulate={(cents) => onSimulate(row.sellerSku, cents)}
              reasonTag={reasonTags.get(row.sellerSku) ?? null}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// State-line chip (toolbar streamlining, Task B) — a small removable pill
// reflecting one piece of toolbar state (scope, filter, sort, search, the
// tried-price reset). Every chip is a single clickable button with an
// explicit aria-label describing what the click does, per the design
// doc's "chips, removable where it makes sense."
// ─────────────────────────────────────────────────────────

function Chip({
  children,
  onClick,
  ariaLabel,
  accent,
}: {
  children: ReactNode;
  onClick: () => void;
  ariaLabel: string;
  accent?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className={cn(
        'inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full border transition-colors duration-fast',
        accent
          ? 'bg-accent-primary/10 border-accent-primary/40 text-accent-fg hover:bg-accent-primary/15'
          : 'bg-surface-elev border-border-subtle text-fg-secondary hover:text-fg-primary hover:border-border-default'
      )}
    >
      {children}
    </button>
  );
}

// ─────────────────────────────────────────────────────────
// Live scenario summary (velocity + scenario plan Task 3; copy rewritten
// for founder UAT findings 2 + 3, 2026-08-30) — the design doc's page
// hierarchy item 2: exact selected units, projected revenue/profit at the
// CURRENT scenario prices (live price, or a tried price for any row in the
// map), the delta vs current prices, blended runway, and data coverage/
// exclusions. Recomputes synchronously on every scope change or price edit
// — no fetch, no persistence.
//
// Founder UAT (2026-08-30): "the top row, the summary, is now confusing. I
// don't know what it means if I sell the inventory in two months for this
// price." Every card label is now a plain question a seller would ask
// ("Units in view," "Profit if it all sells at these prices," "Change from
// today's prices," "Time to sell what you have"); internal terms ("blended
// runway," "valued coverage") moved out of the primary label into the small
// helper line under each value. Every honesty qualifier (at the same sales
// rate; excluded counts; the straight-line-estimate caveat) is unchanged,
// just relocated. "Selected units" also renamed with an explicit subtext
// answering the founder's literal question ("what does selected units
// mean? ... all the pages, all the units are in the current selection?") —
// yes: the sum of in-scope units across every SKU currently included by
// scope + filters, wired from the real included-row count, not a guess.
// ─────────────────────────────────────────────────────────

function ScenarioSummaryCards({
  summary,
  includedRowCount,
}: {
  summary: PriceCalibrationScenarioSummary;
  includedRowCount: number;
}) {
  const { financial, thirtyDay } = summary.aggregate;
  const { slowMovingRowCount, unknownVelocityRowCount } = summary.velocityCoverage;

  return (
    <div className="mt-3">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryTile label="Units in view" value={summary.selectedUnits.toLocaleString('en-US')}>
          <p className="text-xs text-fg-tertiary mt-0.5">
            across {includedRowCount.toLocaleString('en-US')} product
            {includedRowCount === 1 ? '' : 's'} in the current scope and filters
          </p>
          <p className="text-xs text-fg-tertiary mt-1 tabular-nums">
            {financial.valuedRowCount} SKU{financial.valuedRowCount === 1 ? '' : 's'} priced
            {financial.excludedRowCount > 0 &&
              `, ${financial.excludedUnits.toLocaleString('en-US')} unit${
                financial.excludedUnits === 1 ? '' : 's'
              } across ${financial.excludedRowCount} SKU${
                financial.excludedRowCount === 1 ? '' : 's'
              } excluded (missing price, cost, or fee data)`}
          </p>
        </SummaryTile>

        <SummaryTile
          label="Profit if it all sells at these prices"
          // A valuedRowCount of 0 means the sum below is "zero terms," not
          // a known $0 — money(0) would misread as "you'd make nothing"
          // instead of "no priced/costed rows yet."
          value={
            financial.valuedRowCount > 0 ? money(financial.remainingInventoryProfitCents) : '—'
          }
        >
          <p className="text-xs text-fg-tertiary mt-0.5 tabular-nums">
            {financial.valuedRowCount > 0 ? money(financial.remainingInventoryRevenueCents) : '—'}{' '}
            in revenue
          </p>
        </SummaryTile>

        <SummaryTile
          label="Change from today's prices"
          value={
            financial.valuedRowCount > 0 ? signedMoney(financial.sellThroughProfitDeltaCents) : '—'
          }
        >
          <p className="text-xs text-fg-tertiary mt-0.5">at the same sales rate</p>
        </SummaryTile>

        <SummaryTile label="Time to sell what you have" value={summary.blendedRunway.durationLabel}>
          <p className="text-xs text-fg-tertiary mt-0.5">{summary.blendedRunway.qualifier}</p>
        </SummaryTile>
      </div>

      <p className="text-xs text-fg-tertiary mt-2">
        {thirtyDay.valuedRowCount} SKU{thirtyDay.valuedRowCount === 1 ? '' : 's'} with a known
        30-day profit ({thirtyDay.valuedRowCount > 0 ? money(thirtyDay.profitCents) : '—'}) ·{' '}
        {slowMovingRowCount} slow-moving ({'>'}90 days at the current rate) ·{' '}
        {unknownVelocityRowCount} with unknown velocity
      </p>
    </div>
  );
}

function SummaryTile({
  label,
  value,
  children,
}: {
  label: string;
  value: string;
  children?: ReactNode;
}) {
  return (
    <div className="bg-surface border border-border-subtle rounded-xl p-5">
      <p className="text-xs text-fg-tertiary">{label}</p>
      <p className="font-display text-2xl text-fg-primary mt-1.5 tabular-nums">{value}</p>
      {children}
    </div>
  );
}
